/*
 * Copyright (c) 2022. by Honeywell International Inc.All rights reserved.
 */

import { Button, Grid, Icon } from '@scuf/common';
import React from 'react';
import { IThresholdRequest, IThresholdValues } from './IAlarmConfiguration';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../locales/en-US/descriptors';

import ThresholdSection from './ThresholdSection';

import '../../scss/AlarmConfigurationsVariables.scss';

interface IAlarmThresholdFormProps {
  header: string;
  onSave(request: IThresholdRequest[]): void;
  isMetersSelected: boolean;
  children: React.ReactElement;
}

const defaultHighAlarmValues: IThresholdValues = {
  AlarmName: '',
  MaxThresholdValue: '9999',
  MinThresholdValue: ''
};

const defaultMediumAlarmValues: IThresholdValues = {
  AlarmName: '',
  MaxThresholdValue: '',
  MinThresholdValue: ''
};

const defaultLowAlarmValues: IThresholdValues = {
  AlarmName: '',
  MaxThresholdValue: '',
  MinThresholdValue: ''
};

const AlarmThresholdForm: React.FC<IAlarmThresholdFormProps> = (
  props: IAlarmThresholdFormProps
) => {
  const [lowPriorityEnabled, setLowPriorityEnabled] = React.useState(true);
  const [lowPriority, setLowPriority] = React.useState<IThresholdValues>(
    defaultLowAlarmValues
  );
  const [mediumPriorityEnabled, setMediumPriorityEnabled] = React.useState(
    true
  );
  const [mediumPriority, setMediumPriority] = React.useState<IThresholdValues>(
    defaultMediumAlarmValues
  );
  const [highPriorityEnabled, setHighPriorityEnabled] = React.useState(true);
  const [highPriority, setHighPriority] = React.useState<IThresholdValues>(
    defaultHighAlarmValues
  );
  const highPriorityFieldsCheck =
    !highPriority.AlarmName ||
    !highPriority.MinThresholdValue ||
    !highPriority.MaxThresholdValue;
  const mediumPriorityFieldsCheck =
    !mediumPriority.AlarmName ||
    !mediumPriority.MinThresholdValue ||
    !mediumPriority.MaxThresholdValue;
  const lowPriorityFieldsCheck =
    !lowPriority.AlarmName ||
    !lowPriority.MinThresholdValue ||
    !lowPriority.MaxThresholdValue;

  const d = descriptors.alarmConfiguration;
  const [t] = useTranslation();

  const handleInputChange = (
    severity: string,
    field: string,
    value: boolean | string
  ) => {
    if (severity === 'HIGH')
      if (field === 'enabled') setHighPriorityEnabled(value === true);
      else setHighPriority({ ...highPriority, [field]: value });
    else if (severity === 'MEDIUM')
      if (field === 'enabled') setMediumPriorityEnabled(value === true);
      else setMediumPriority({ ...mediumPriority, [field]: value });
    else if (field === 'enabled') setLowPriorityEnabled(value === true);
    else setLowPriority({ ...lowPriority, [field]: value });
  };

  const saveAlarmConfiguration = () => {
    const configurations: IThresholdRequest[] = [];
    if (highPriorityEnabled) {
      configurations.push({ Priority: 'HIGH', Values: highPriority });
    }
    if (mediumPriorityEnabled) {
      configurations.push({ Priority: 'MEDIUM', Values: mediumPriority });
    }
    if (lowPriorityEnabled) {
      configurations.push({ Priority: 'LOW', Values: lowPriority });
    }
    props.onSave(configurations);
  };

  const handleValidation = () => {
    if (!lowPriorityEnabled && !mediumPriorityEnabled && !highPriorityEnabled) {
      return t(d.VALIDATIONREQUIRED);
    }
    if (checkForRequiredFields('HIGH')) {
      return t(d.VALIDATIONREQUIRED);
    }
    if (checkForRequiredFields('MEDIUM')) {
      return t(d.VALIDATIONREQUIRED);
    }
    if (checkForRequiredFields('LOW')) {
      return t(d.VALIDATIONREQUIRED);
    }

    if (checkForValuesOverlapping()) {
      return t(d.VALIDATIONOVERLAPPING);
    }
    return '';
  };

  const checkForRequiredFields = (type: 'HIGH' | 'MEDIUM' | 'LOW') => {
    switch (type) {
      case 'HIGH':
        return highPriorityEnabled && highPriorityFieldsCheck;
      case 'MEDIUM':
        return mediumPriorityEnabled && mediumPriorityFieldsCheck;
      case 'LOW':
      default:
        return lowPriorityEnabled && lowPriorityFieldsCheck;
    }
  };

  const checkIfValuesAreValid = (
    lowMin: number,
    lowMax: number,
    medMin: number,
    medMax: number,
    highMin: number,
    highMax: number
  ) => {
    const lowPriorityValidation =
      lowPriorityEnabled && (lowMin <= 0 || lowMax <= lowMin);
    const mediumPriorityValidation =
      mediumPriorityEnabled && (medMin <= 0 || medMax <= medMin);
    const highPriorityValidation =
      highPriorityEnabled && (highMin <= 0 || highMax <= highMin);

    return (
      lowPriorityValidation ||
      mediumPriorityValidation ||
      highPriorityValidation
    );
  };

  const checkForValuesOverlapping = () => {
    const lowMin = parseInt(lowPriority.MinThresholdValue, 10),
      lowMax = parseInt(lowPriority.MaxThresholdValue, 10),
      medMin = parseInt(mediumPriority.MinThresholdValue, 10),
      medMax = parseInt(mediumPriority.MaxThresholdValue, 10),
      highMin = parseInt(highPriority.MinThresholdValue, 10),
      highMax = parseInt(highPriority.MaxThresholdValue, 10);

    const lowMediumValidation =
      lowPriorityEnabled && mediumPriorityEnabled && medMin < lowMax;
    const lowHighValidation =
      lowPriorityEnabled && highPriorityEnabled && highMin < lowMax;
    const mediumHighValidation =
      mediumPriorityEnabled && highPriorityEnabled && highMin < medMax;

    const interMediateValidation =
      lowMediumValidation || lowHighValidation || mediumHighValidation;

    return (
      checkIfValuesAreValid(lowMin, lowMax, medMin, medMax, highMin, highMax) ||
      interMediateValidation
    );
  };

  const resetToDefaults = () => {
    setHighPriorityEnabled(true);
    setHighPriority(defaultHighAlarmValues);
    setMediumPriorityEnabled(true);
    setMediumPriority(defaultMediumAlarmValues);
    setLowPriorityEnabled(true);
    setLowPriority(defaultLowAlarmValues);
  };

  const getHeaderContent = () => {
    if (props.isMetersSelected) {
      return (
        <>
          {handleValidation() && (
            <div className="threshold-form-error">
              <Icon name="badge-info" root="common" />
              <span>{handleValidation()}</span>
            </div>
          )}
          {renderAlarmThresholdSection()}
        </>
      );
    } else {
      return (
        <div className="no-meters-section">
          <p className="no-meters-text">{t(d.NOMETERSSELECTED)}</p>
        </div>
      );
    }
  };

  const renderAlarmThresholdSection = () => {
    return (
      <Grid>
        <ThresholdSection
          enabled={highPriorityEnabled}
          values={highPriority}
          severity="HIGH"
          onChange={(field, value) => handleInputChange('HIGH', field, value)}
        ></ThresholdSection>
        <ThresholdSection
          enabled={mediumPriorityEnabled}
          values={mediumPriority}
          severity="MEDIUM"
          onChange={(field, value) => handleInputChange('MEDIUM', field, value)}
        ></ThresholdSection>
        <ThresholdSection
          enabled={lowPriorityEnabled}
          values={lowPriority}
          severity="LOW"
          onChange={(field, value) => handleInputChange('LOW', field, value)}
        ></ThresholdSection>
      </Grid>
    );
  };

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column
          width={3}
          className="meters-section-column no-padding-bottom"
        >
          <div className="meters-section">
            <span>{t(d.ALLMETERS)}</span>
            {props.children}
          </div>
        </Grid.Column>
        <Grid.Column width={9} className="no-padding-bottom">
          <div className="threshold-section">
            <>
              <h5 className="threshold-section-header">{props.header}</h5>
              {getHeaderContent()}
            </>
          </div>
        </Grid.Column>
      </Grid.Row>
      {props.isMetersSelected && (
        <div className="buttons-section">
          <Grid.Row>
            <Grid.Column width={12} className="buttons">
              <Button type="link" onClick={resetToDefaults}>
                {t(d.RESETTODEFAULTS)}
              </Button>
              <Button
                type="primary"
                disabled={handleValidation() !== ''}
                onClick={saveAlarmConfiguration}
              >
                {t(d.SAVE)}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </div>
      )}
    </Grid>
  );
};

export default AlarmThresholdForm;
